import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaUtensils, FaLink, FaStar } from 'react-icons/fa';
import { GiPayMoney } from 'react-icons/gi';
import ErrorPage from '../../components/Error';
import Footer from '../../components/Footer';
import { trackEvent } from '../../utils/analytics';
import { motion } from 'framer-motion';

const TeamPage = () => {
  const { id } = useParams();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [isButton, setIsButton] = useState(false);
  const [buttonName, setButtonName] = useState('');
  const [buttonLink, setButtonLink] = useState('');
  const [LinkAvis, setLinkAvis] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const languages = [
    { code: 'fr', label: 'FR', flag: '🇫🇷' },
    { code: 'en', label: 'EN', flag: '🇬🇧' },
    { code: 'es', label: 'ES', flag: '🇪🇸' },
    { code: 'it', label: 'IT', flag: '🇮🇹' },
    { code: 'de', label: 'DE', flag: '🇩🇪' },
    { code: 'zh', label: 'ZH', flag: '🇨🇳' },
    { code: 'ru', label: 'RU', flag: '🇷🇺' },
    { code: 'uk', label: 'UK', flag: '🇺🇦' },
    { code: 'pt', label: 'PT', flag: '🇵🇹' },
    { code: 'ko', label: 'KO', flag: '🇰🇷' },
    { code: 'ja', label: 'JA', flag: '🇯🇵' },
    { code: 'ar', label: 'AR', flag: '🇸🇦' },
    { code: 'sv', label: 'SV', flag: '🇸🇪' },
    { code: 'no', label: 'NO', flag: '🇳🇴' },
  ];

  const currentLang = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'fr';
  const currentFlag = languages.find(l => l.code === currentLang)?.flag || '🇫🇷';

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/Equipe/${id}`)
      .then(response => response.json())
      .then(data => {
        if (
          data.liste_membre.length === 1 &&
          data.liste_membre[0].Prenom === 'Equipe inexistante' &&
          data.liste_membre[0].Nom === 'Equipe inexistante' &&
          data.liste_membre[0].Lien === 'Equipe inexistante'
        ) {
          throw new Error('teamNotFound');
        }

        setMembers(data.liste_membre);
        localStorage.setItem("LastTeamId", id);
        setFilteredMembers(data.liste_membre);
        setIsButton(data.isButton);
        setButtonName(data.buttonName);
        setButtonLink(data.buttonLink);

        // Stocker la devise de l'equipe
        if (data.currency) {
          localStorage.setItem("teamCurrency", data.currency.toLowerCase());
          localStorage.setItem("teamCurrencySymbol", data.currencySymbol || '€');
        }

        if (data.liste_membre.length > 0) {
          setTeamName(data.liste_membre[0].Nom || '');
        }

        if (data.LinkAvis && data.LinkAvis.trim() !== "") {
          setLinkAvis(data.LinkAvis);
          localStorage.setItem("LinkAvis", data.LinkAvis);
        } else {
          localStorage.removeItem("LinkAvis");
        }

        // Rediriger directement vers les membres si pas de bouton ni d'avis
        if (!data.isButton && (!data.LinkAvis || data.LinkAvis.trim() === "")) {
          setShouldRedirect(true);
        }

        setLoading(false);
      })
      .catch(error => {
        setError(error.message);
        setLoading(false);
      });
  }, [id]);

  const openLinkInNewTab = () => {
    trackEvent('open_link', 'navigation', 'Ouverture link team');
    window.open(buttonLink, '_blank', "noopener,noreferrer");
  };

  const openAvisInNewTab = () => {
    trackEvent('click_leave_review', 'avis', `Avis equipe ${id}`);
    window.open(LinkAvis, '_blank', 'noopener,noreferrer');
  };

  const handleViewMembers = () => {
    trackEvent('visit_tip_page', 'engagement', `Equipe ${id}`);
    navigate(`/team/${id}/members`, { state: { members, filteredMembers } });
  };

  // Redirection automatique si pas de bouton ni d'avis
  useEffect(() => {
    if (shouldRedirect && !loading && members.length > 0) {
      trackEvent('visit_tip_page', 'engagement', `Equipe ${id}`);
      navigate(`/team/${id}/members`, { state: { members, filteredMembers }, replace: true });
    }
  }, [shouldRedirect, loading, members, filteredMembers, id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-modern flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-6" />
          <p className="text-gray-500 font-medium">{t('teamTitle')}</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return <ErrorPage message={error} />;
  }

  if (shouldRedirect) {
    return null;
  }

  const hasMenu = isButton && buttonName !== "" && buttonLink !== "";
  const hasAvis = LinkAvis && LinkAvis.trim() !== "";

  return (
    <div className="relative no-scroll min-h-screen bg-modern text-black flex flex-col justify-between overflow-hidden">

      {/* Contenu principal */}
      <div className="flex flex-col items-center min-h-screen">

        {/* Header avec nom equipe + langue */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-screen bg-white rounded-b-[2rem] shadow-lg px-6 pt-10 pb-8 text-center mb-8 relative"
        >
          {/* Language switcher */}
          <div className="absolute top-4 right-4" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 text-sm font-medium px-3 py-1.5 rounded-full transition"
            >
              <span>{currentFlag}</span>
              <span className="text-xs text-gray-600">{currentLang.toUpperCase()}</span>
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-24 max-h-60 overflow-y-auto z-50">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-1.5 text-sm transition ${
                      currentLang === lang.code
                        ? 'bg-black text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-black mb-1">
            TipsYou
          </h1>
          <p className="text-gray-400 text-sm">
            {teamName || t('contactless_tips')}
          </p>
        </motion.div>

        {/* Boutons */}
        <div className="flex flex-col items-center w-full px-6 space-y-4 max-w-lg flex-1">

          {/* CTA Pourboire */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={handleViewMembers}
            className="w-full px-6 py-5 font-bold bg-black text-white rounded-2xl shadow-lg transition text-lg flex items-center justify-center gap-3 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
          >
            <GiPayMoney size={28} />
            <span>{t('tipButton')}</span>
          </motion.button>

          {/* Bouton menu/lien */}
          {hasMenu && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              onClick={openLinkInNewTab}
              className="w-full px-6 py-5 font-bold bg-white text-black rounded-2xl shadow-md border border-gray-100 transition text-lg flex items-center justify-center gap-3 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              {buttonName.toLowerCase().includes('menu') ? (
                <FaUtensils size={24} className="text-orange-500" />
              ) : (
                <FaLink size={24} className="text-blue-500" />
              )}
              <span>{buttonName}</span>
            </motion.button>
          )}

          {/* Bouton avis Google */}
          {hasAvis && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: hasMenu ? 0.5 : 0.35 }}
              onClick={openAvisInNewTab}
              className="w-full px-6 py-5 font-bold bg-white text-black rounded-2xl shadow-md border border-gray-100 transition text-lg flex items-center justify-center gap-3 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
            >
              <FaStar size={24} className="text-yellow-500" />
              <span>{t('leaveReviewButton')}</span>
            </motion.button>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default TeamPage;
